import { Flex } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { keyframes } from 'styled-components'
import { FontStyle, Text } from '~/components/core'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  QuickActionButton,
  QuickActionButtonSizes,
} from '~/components/shared/QuickActionButton'
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover'
import { GeneralOperation } from '~/hooks/toast/eventHandlers/generalToastEventHandler'
import { useEmitterService } from '~/hooks/useEmitterService'
import { useUserClickedExternalElements } from '~/hooks/useUserClickedExternalElements'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'

const BoxAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`
const BoxWrapper = styled.div`
  display: inline-flex;
  box-shadow: 0px 12px 60px rgba(0, 43, 64, 0.12);
  border-radius: 4px;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  height: 100%;
  width: 100%;
  user-select: none;
  animation: ${BoxAnimation} 0.25s ease-in;
`
const InnerBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  width: 100%;
  align-items: center;
  white-space: nowrap;
`

const LinkWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 8px;
  height: 35px;
  cursor: pointer;
  &:hover {
    background-color: var(--chakra-colors-bg-subtle);
  }
`

const LINK_COPIED_TIMER_DURATION_MS = 1500

export interface CopyLinkToClipboardFabIconProps {
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  timeStamp?: string
  size?: QuickActionButtonSizes
  onCopyLink?: () => void
  onCopyLinkAtTimestamp?: () => void
}
export const CopyLinkToClipboardFabIcon = ({
  icon,
  timeStamp,
  size,
  onCopyLink,
  onCopyLinkAtTimestamp,
}: CopyLinkToClipboardFabIconProps) => {
  const { emit } = useEmitterService()
  const [isWindowOpen, setIsWindowOpen] = useState(false)
  const [linkCopiedOpen, setLinkCopiedOpen] = useState(false)
  const urlSelectionBoxRef = React.useRef<HTMLDivElement>(null)
  const fabIconRef = React.useRef<HTMLButtonElement>(null)
  const { t } = useTranslation()
  const bothRefsPresent = urlSelectionBoxRef.current && fabIconRef.current

  useUserClickedExternalElements(
    bothRefsPresent ? [urlSelectionBoxRef.current, fabIconRef.current] : [],
    true,
    () => {
      setIsWindowOpen(false)
    },
  )

  const sendCopyNotification = () => {
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.GENERAL,
      operation: GeneralOperation.CopyLinkToClipboard,
    })
  }

  const copyAndClose = () => {
    onCopyLink?.()
    setIsWindowOpen(false)
    sendCopyNotification()
  }

  const copyAtTimestampAndClose = () => {
    onCopyLinkAtTimestamp?.()
    setIsWindowOpen(false)
    sendCopyNotification()
  }

  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (linkCopiedOpen) {
      timeout = setTimeout(() => {
        setLinkCopiedOpen(false)
      }, LINK_COPIED_TIMER_DURATION_MS)
    }

    if (isWindowOpen && linkCopiedOpen) {
      setLinkCopiedOpen(false)
      clearTimeout(timeout!)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [isWindowOpen, linkCopiedOpen])

  const tooltipText =
    isWindowOpen || linkCopiedOpen ? '' : t('global.tooltip.copyLink')

  return (
    <>
      <Popover
        isOpen={linkCopiedOpen || isWindowOpen}
        returnFocusOnClose={false}
      >
        <PopoverTrigger asChild>
          <QuickActionButton
            ref={fabIconRef}
            variant={QuickActionButtonVariants.Default_200}
            size={size}
            iconProps={{
              boxSize: '1.5rem',
            }}
            actionIcon={icon}
            tooltipText={tooltipText}
            aria-label={tooltipText}
            onClick={() => {
              if (!timeStamp || timeStamp === '00:00') {
                copyAndClose()
                return
              }
              setIsWindowOpen(!isWindowOpen)
            }}
          />
        </PopoverTrigger>
        <PopoverContent width="fit-content" bg="transparent">
          <PopoverBody minWidth="fit-content" padding="0">
            <Flex
              key="menu"
              ref={urlSelectionBoxRef}
              height="94px"
              bg="bg.panel"
              zIndex={1000}
              hidden={!isWindowOpen}
            >
              <BoxWrapper>
                <InnerBoxWrapper>
                  <LinkWrapper onClick={copyAndClose}>
                    <Text
                      fontStyle={FontStyle.Normal}
                      color="var(--chakra-colors-fg-default)"
                    >
                      {t('global.tooltip.copyLink')}
                    </Text>
                  </LinkWrapper>
                  <LinkWrapper onClick={copyAtTimestampAndClose}>
                    <Text
                      fontStyle={FontStyle.Normal}
                      color="var(--chakra-colors-fg-default)"
                    >
                      {t('global.tooltip.copyLinkAt', { timeStamp })}
                    </Text>
                  </LinkWrapper>
                </InnerBoxWrapper>
              </BoxWrapper>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </>
  )
}
